import type { HistoryItem } from "@scoach/types";
import { Brain, Trash } from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { dashboardApi } from "../dashboard/api.ts";
import { DashHeader } from "../dashboard/components/DashHeader.tsx";
import { SimulationTraining } from "./SimulationTraining.tsx";

function formatHistoryDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { date, time };
}

export function SimulationsScreen() {
  const nav = useNavigate();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  useEffect(() => {
    Promise.all([
      dashboardApi.fetchHistory({ scope: "mine" }),
      dashboardApi.fetchHistory({ scope: "shared" }),
    ]).then(([mine, shared]) => {
      setHistory([...mine.items, ...shared.items]);
    }).catch(() => {});
  }, []);

  const simulations = useMemo(() => {
    return history
      .filter((m) => m.meetingType === "simulation")
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [history]);

  function openMeeting(id: string, status?: string) {
    if (status === "draft") {
      nav({ to: "/meetings/$id/setup", params: { id } });
    } else if (status === "live") {
      nav({ to: "/meetings/$id/live", params: { id } });
    } else {
      nav({ to: "/meetings/$id/summary", params: { id } });
    }
  }

  function deleteMeeting(id: string, client: string) {
    if (!confirm(`Delete simulation with ${client || "this client"}? This cannot be undone.`)) return;
    setHistory((prev) => prev.filter((m) => m.id !== id));
    void dashboardApi.deleteMeeting(id).catch(() => {
      dashboardApi.fetchHistory({}).then((r) => setHistory(r.items)).catch(() => {});
    });
  }

  const startNew = useCallback(async () => {
    const d = new Date();
    const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    const m = await dashboardApi.createMeeting({
      account: { name: "" },
      title: `Meeting — ${date}, ${time}`,
      stage: "Intro",
    });
    nav({ to: "/meetings/$id/setup", params: { id: m.id } });
  }, [nav]);

  return (
    <div className="dash">
      <DashHeader onStartNew={startNew} startLabel="New Simulation" />

      <div className="dash-grid">
        <section className="dash-history" style={{ gridColumn: "1 / -1" }}>
          <SimulationTraining />

          <div className="dash-history-head" style={{ marginTop: 32 }}>
            <h2 className="dash-h2">Past simulations</h2>
          </div>

          <div className="hist-table">
            <div className="hist-row hist-head-row">
              <div>Date</div>
              <div>Client</div>
              <div>Scenario</div>
              <div>Stage</div>
              <div>Score</div>
              <div></div>
            </div>
            {simulations.map((m) => {
              const { date, time } = formatHistoryDate(m.date);
              return (
                <button
                  key={m.id}
                  type="button"
                  className="hist-row"
                  onClick={() => openMeeting(m.id, m.status)}
                >
                  <div className="hist-date-col">
                    <div className="hist-date mono">{date}</div>
                    <div className="hist-time mono">{time} · {m.duration}</div>
                  </div>
                  <div className="hist-client">
                    <div className="hist-avatar" style={{ background: m.avatar }}>
                      {m.client?.[0] || "S"}
                    </div>
                    <div className="hist-client-name">{m.client || "—"}</div>
                  </div>
                  <div className="hist-title">
                    <span>{m.title}</span>
                    {m.status === "live" && (
                      <span style={{
                        marginLeft: 8, padding: "2px 6px", borderRadius: 4,
                        fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                        background: "var(--gc-green-50, #e6f4ea)", color: "var(--gc-green, #1e8e3e)",
                      }}>
                        ● Live · resume
                      </span>
                    )}
                    {m.status === "draft" && (
                      <span style={{
                        marginLeft: 8, padding: "2px 6px", borderRadius: 4,
                        fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                        background: "var(--gc-yellow-50, #fff7e0)", color: "var(--gc-yellow, #b06000)",
                      }}>
                        Draft
                      </span>
                    )}
                  </div>
                  <div>
                    <span className={`stage-pill stage-${m.stage.toLowerCase()}`}>{m.stage}</span>
                  </div>
                  <div className="hist-hints mono">
                    {m.score > 0 ? (
                      <span style={{
                        fontWeight: 600,
                        color: m.score >= 75 ? "var(--gc-green, #1e8e3e)" : m.score >= 50 ? "var(--gc-yellow, #b06000)" : "var(--gc-red, #d93025)",
                      }}>
                        {m.score}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-4)" }}>—</span>
                    )}
                  </div>
                  <div className="hist-cta" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="hist-delete-btn"
                      title="Delete simulation"
                      onClick={() => deleteMeeting(m.id, m.client)}
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                </button>
              );
            })}
            {simulations.length === 0 && (
              <div className="hist-empty">
                <Brain size={28} />
                <div className="hist-empty-title">No simulations yet</div>
                <div className="hist-empty-sub">
                  Choose a scenario above to start your first simulation training.
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
